import classNames from "classnames";

import { Badge } from "@jti/core-components/components/Badge";
import { Skeleton } from "@jti/core-components/components/Skeleton";
import { type ProductPriceType } from "@jti/fe-common";
import { interpolateCopy } from "@jti/fe-common/utils";
import { formatPrice } from "@jti/format-price";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { FormattedPrice } from "@jti/ui/components/FormattedPrice";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { LoyaltyBadgeCopyConfig, LoyaltyPriceTagState } from "@product-details/components/ProductLoyaltyBlock";

import styles from "./ProductPrice.module.scss";

type ProductPriceProps = {
  hideDiscountBadge: boolean;
  hideLoyaltyDiscountBadge?: boolean;
  hideStrikethroughPrice: boolean;
  priceInformation: ProductPriceType | null;
  discount: number | null;
  priceClass?: string;
  isFetching?: boolean;
  showMonthlyBadge?: boolean;
  productDisclaimer?: string | null;
  loyaltyPriceTagState?: LoyaltyPriceTagState;
  loyaltyBadgeCopy?: LoyaltyBadgeCopyConfig;
};

const getLoyaltyPriceTagText = (
  loyaltyPriceTagState: LoyaltyPriceTagState | undefined,
  loyaltyBadgeCopy: LoyaltyBadgeCopyConfig | undefined,
  currency?: string
): string | null => {
  if (!loyaltyPriceTagState || loyaltyPriceTagState.status === "hidden") {
    return null;
  }

  if (loyaltyPriceTagState.status === "anonymous") {
    const anonymousDiscountTag = loyaltyBadgeCopy?.anonymousDiscountTag;

    if (!anonymousDiscountTag) {
      return null;
    }

    return interpolateCopy(anonymousDiscountTag, {
      discount: "",
      discountAmount: "",
      discountPoints: "",
    });
  }

  const discountAmount = currency
    ? formatPrice({
        currency,
        value: loyaltyPriceTagState.discountAmount,
      })
    : String(loyaltyPriceTagState.discountAmount);

  const loggedInPartialDiscountTag = loyaltyBadgeCopy?.loggedInPartialDiscountTag;

  if (!loggedInPartialDiscountTag) {
    return null;
  }

  return interpolateCopy(loggedInPartialDiscountTag, {
    discount: discountAmount,
    discountAmount: loyaltyPriceTagState.discountAmount,
    discountPoints: loyaltyPriceTagState.discountPoints,
  });
};

export const calculateHasDiscountPrice = (
  priceInformation: ProductPriceType | null,
  hideStrikethroughPrice: boolean
) => {
  if (
    !hideStrikethroughPrice &&
    typeof priceInformation?.finalPrice !== "undefined" &&
    typeof priceInformation?.price !== "undefined" &&
    priceInformation.finalPrice.value !== priceInformation.price.value &&
    priceInformation.price.value
  ) {
    return true;
  }
  return false;
};

export const ProductPrice = ({
  hideDiscountBadge,
  hideLoyaltyDiscountBadge = false,
  hideStrikethroughPrice,
  priceInformation,
  discount,
  priceClass,
  isFetching = false,
  showMonthlyBadge,
  productDisclaimer,
  loyaltyPriceTagState,
  loyaltyBadgeCopy,
}: ProductPriceProps) => {
  const { __ } = useTranslations();
  const hasDiscountPrice = calculateHasDiscountPrice(priceInformation, hideStrikethroughPrice);
  const loyaltyPriceTagText = getLoyaltyPriceTagText(
    loyaltyPriceTagState,
    loyaltyBadgeCopy,
    priceInformation?.finalPrice?.currency
  );
  const hasLoyaltyDiscountBadge = !hideLoyaltyDiscountBadge;
  const hasDiscountBadge = !hideDiscountBadge && Boolean(discount);

  return (
    <div className={classNames("productDetails__rowContainer--row", styles.container)}>
      {isFetching ? (
        <DEPRECATED_Loading dataTestId="product-price-loader" />
      ) : (
        <>
          <div className={styles.pricesAndBadges}>
            <p className={classNames(["productDetails__prices", priceClass])}>
              {hasDiscountPrice && priceInformation?.price !== undefined && (
                <s className="productDetails__oldPrice" data-testid="regularPrice" data-striked-price>
                  <FormattedPrice {...priceInformation?.price} />
                </s>
              )}

              <span className="productDetails__newPrice" data-testid="finalPrice" data-price>
                {priceInformation?.finalPrice !== undefined && <FormattedPrice {...priceInformation?.finalPrice} />}
                {showMonthlyBadge && `/${__("product.info.monthlyPriceLabel")}`}
              </span>
            </p>

            {(hasLoyaltyDiscountBadge || hasDiscountBadge) && (
              <div
                className={classNames("aem-productDetails__priceBadges", styles.priceBadges)}
                data-testid="product-price-badges"
              >
                {hasDiscountBadge && <Badge label={`-${discount}%`} data-testid="discountValue" />}

                {hasLoyaltyDiscountBadge && (
                  <>
                    {loyaltyPriceTagText && loyaltyPriceTagState?.status !== "hidden" ? (
                      <Badge
                        variant="neutral-subtle"
                        label={loyaltyPriceTagText ?? ""}
                        data-testid="loyaltyDiscountValue"
                      />
                    ) : (
                      <div data-testid="loyaltyDiscount-loading" className={styles.loyaltyDiscountBadgeLoading}>
                        <Skeleton height="28px" />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {!!productDisclaimer && (
            <Typography variant={"body1"} className="product-details__disclaimer">
              {productDisclaimer}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};
